import { Container, Sprite } from 'pixi.js';
import { SolitaireGame } from './SolitaireGame';
import { getAsset } from './preloadGameAssets';
import { Card } from 'components/Card';
import { areBoxesCollide } from './helpers/collision';
import { ACES_VALUE, COMPLETE_COLOR_COUNT, DropCardPlace, GamePlace, KING_VALUE } from 'constants/cards';
import { Layout } from './LayoutManager';
import { FOUNDATION_Z_INDEX } from 'constants/layout';
import { CardParticles } from './CardParticles';
import { CARD_MOVE_TIME } from 'constants/move';
import gsap, { Power1 } from 'gsap';
import { Store } from './store/gameStore';
import { GameName } from 'constants/difficulty';
import { SpiderParticles } from './SpiderParticles';
import { SoundManager } from './core/soundManager';
import { GameBoardPlace } from './GameBoardPlace';

export class Foundation extends GameBoardPlace {
    placeholder: Container;
    placeholderSprite?: Sprite;

    constructor(parent: SolitaireGame, index: number) {
        super(parent, index, GamePlace.FOUNDATION);
        this.addCard = this.addCard.bind(this);
        this.placeholder = new Container();
        this.container.addChild(this.placeholder);
        this.drawPlaceholder();
        this.resize();
    }

    async drawPlaceholder() {
        const txt = await getAsset('placeholders/slot-ace');

        this.placeholderSprite = Sprite.from(txt);
        this.placeholderSprite.anchor.set(0.5);

        this.placeholder.addChild(this.placeholderSprite);
        this.redrawPlaceholder();
    }
    redrawPlaceholder() {
        if (!this.placeholderSprite) return;
        const { width, height } = Layout.getCardSize();
        this.placeholderSprite.height = height;
        this.placeholderSprite.width = width;
    }
    removeCard(removeCard: Card) {
        this.cards = this.cards.filter((c) => c._id !== removeCard._id);
    }

    overDropArea(container: Container) {
        if (!this.lastCard) return false;
        return areBoxesCollide(this.lastCard.container, container);
    }

    refresh() {
        const position = Layout.getFoundationPosition(this.index);
        gsap.to(this.container.position, {
            ...position,
            ease: Power1.easeOut,
            duration: CARD_MOVE_TIME,
        });
        this.redrawPlaceholder();
        this.cards.forEach((card, index) => {
            card.place.zIndex = index + FOUNDATION_Z_INDEX;
            card.place.position = position;
            card.place.order = index;
            card.moveTo(position);

            if (Store.isKlondike) card.setActive(this.cards.length - 1 === index);
            if (Store.isFreecell) card.setActive(this.cards.length - 1 === index);
            if (Store.isSpider) card.setActive(false);

            card.cardDisplay.castShadow(index === 0);
        });
    }
    resize(): void {
        const position = Layout.getFoundationPosition(this.index);

        this.container.position = position;
        this.redrawPlaceholder();
        this.cards.forEach((card, index) => {
            card.setPosition(position);
            card.cardDisplay.castShadow(index === 0);
            card.place.zIndex = index + FOUNDATION_Z_INDEX;
        });
    }

    addCard(card: Card, onLoad = false) {
        super.addCard(card);
        card.place.zIndex = this.length + FOUNDATION_Z_INDEX;

        if (onLoad) return;
        switch (Store.name) {
            case GameName.KLONDIKE:
            case GameName.FREECELL:
                new CardParticles(this.container, card.cardColor);
                break;

            case GameName.SPIDER:
                this.game.refreshAllHighlight();
                this.game.refreshBoard();
                break;
        }
    }
    getPlace(): DropCardPlace {
        return { place: GamePlace.FOUNDATION, dropColumnIndex: this.index };
    }
    canDropCard(moveGroup: Card[]) {
        switch (Store.name) {
            case GameName.KLONDIKE:
                if (moveGroup.length > 1) return false;
                if (!this.lastCard) return moveGroup[0].value === ACES_VALUE;
                return this.lastCard.canDropInFoundation(moveGroup[0]);

            case GameName.FREECELL:
                if (moveGroup.length > 1) return false;
                if (!this.lastCard) return moveGroup[0].value === ACES_VALUE;
                return this.lastCard.canDropInFoundation(moveGroup[0]);

            case GameName.SPIDER:
                if (this.lastCard) return false;
                return moveGroup.length === 13;
        }
        return false;
    }

    get isComplete() {
        return this.cards.length === COMPLETE_COLOR_COUNT;
    }

    beforeCardAdded() {
        switch (Store.name) {
            case GameName.KLONDIKE:
            case GameName.FREECELL:
                if (!this.game.autocompleteActivated) this.game.autoPlay();
                break;
        }
    }

    cardAdded(card: Card) {
        switch (Store.name) {
            case GameName.SPIDER:
                if (card.value === KING_VALUE) {
                    if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
                        new SpiderParticles(this.container);
                    } else {
                        new CardParticles(this.container, card.cardColor);
                    }
                    SoundManager.play('winStackSFX');

                    this.game.autoPlayActivated = false;
                    this.game.refreshHighlight(card);
                    this.game.refreshBoard();
                }
                break;
            case GameName.FREECELL:
                this.game.refreshHighlight(card);
                this.game.refreshBoard();
                break;
            case GameName.KLONDIKE:
                this.game.refreshBoard();
        }
        this.refreshShadow();
    }
    refreshShadow() {
        this.cards.forEach((card, index) => {
            card.cardDisplay.castShadow(index === 0);
        });
    }
}
